import React, { useEffect, useState } from "react";
import {
    Card,
    Row,
    Col,
    Typography,
    Button,
    Input,
    Table,
    Space,
    Tooltip,
    Tag,
    Popconfirm,
    message,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../../configs";
import dayjs from "dayjs";
import "dayjs/locale/id";

const { Title, Text } = Typography;

const PaymentReceived = () => {
    const history = useHistory();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [searchText, setSearchText] = useState("");

    const fetchData = async (params = {}) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get("/finance/payment-received", {
                params: {
                    page: params.current || pagination.current,
                    size: params.pageSize || pagination.pageSize,
                    search: searchText,
                    ...params,
                },
            });
            const { content, totalElements } = response.data.data;
            setData(content);
            setPagination({
                ...pagination,
                current: response.data.data.current,
                total: totalElements,
            });
        } catch (error) {
            console.error(error);
            message.error("Gagal mengambil data pembayaran.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleTableChange = (newPagination) => {
        fetchData({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        });
    };

    const handleDelete = async (id) => {
        try {
            await axiosInstance.delete(`/finance/payment-received/${id}`);
            message.success("Data berhasil dihapus");
            fetchData();
        } catch (error) {
            console.error(error);
            message.error("Gagal menghapus data");
        }
    };

    const columns = [
        {
            title: "NO.",
            key: "index",
            width: 60,
            align: "center",
            render: (text, record, index) =>
                (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "TGL. PEMBAYARAN",
            dataIndex: "paymentDate",
            key: "paymentDate",
            render: (text) => (text ? dayjs(text).locale("id").format("DD MMMM YYYY") : "-"),
        },
        {
            title: "PEMBELI",
            dataIndex: "idContact", // Ideally fetch contact name
            key: "idContact",
        },
        {
            title: "NO. PEMBAYARAN",
            dataIndex: "paymentNumber",
            key: "paymentNumber",
        },
        {
            title: "NO. INVOICE",
            dataIndex: "idInvoice", // Ideally fetch invoice number
            key: "idInvoice",
        },
        {
            title: "METODE",
            dataIndex: "paymentMethod",
            key: "paymentMethod",
        },
        {
            title: "JUMLAH",
            dataIndex: "amount",
            key: "amount",
            render: (value) =>
                new Intl.NumberFormat("id-ID", {
                    style: "currency",
                    currency: "IDR",
                }).format(value),
        },
        {
            title: "STATUS",
            dataIndex: "status",
            key: "status",
            render: (status) => {
                let color = "default";
                if (status === "Lunas") color = "success";
                if (status === "Pending") color = "warning";
                return <Tag color={color}>{status}</Tag>;
            },
        },
        {
            title: "AKSI",
            key: "action",
            align: "center",
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit">
                        <Button
                            size="small"
                            type="primary"
                            icon={<EditOutlined />}
                            style={{
                                background: "#3699FF",
                                borderColor: "#3699FF",
                            }}
                            onClick={() => history.push(`/finance/payment-received/form/${record.idPaymentReceived}`)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Apakah Anda yakin ingin menghapus data ini?"
                        onConfirm={() => handleDelete(record.idPaymentReceived)}
                        okText="Ya"
                        cancelText="Batal"
                        okButtonProps={{ danger: true }}
                    >
                        <Tooltip title="Hapus">
                            <Button type="primary" danger size="small" icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col>
                    <Title level={3} style={{ marginBottom: 4 }}>
                        Daftar Penerimaan Pembayaran
                    </Title>
                    <Text type="secondary">
                        Kelola daftar penerimaan pembayaran dari pelanggan.
                    </Text>
                </Col>
                <Col>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        style={{
                            background: "#1BC5BD",
                            borderColor: "#1BC5BD",
                            fontWeight: 600,
                        }}
                        onClick={() => history.push("/finance/payment-received/form")}
                    >
                        Buat Baru
                    </Button>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} md={12}>
                    <Input
                        placeholder="Cari No. Pembayaran..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onPressEnter={() => fetchData({ current: 1 })}
                    />
                </Col>
                <Col xs={24} md={12} style={{ textAlign: "right" }}>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={() => { setSearchText(""); fetchData({ current: 1 }); }}>
                            Reset
                        </Button>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={() => fetchData({ current: 1 })}
                            style={{
                                background: "#8950FC",
                                borderColor: "#8950FC",
                            }}
                        >
                            Cari
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={data}
                rowKey="idPaymentReceived"
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total, range) => `Total ${total} data | ${range[0]}-${range[1]}`,
                }}
                loading={loading}
                onChange={handleTableChange}
                bordered
            />
        </Card>
    );
};

export default PaymentReceived;
